import { Alert, AlertTitle, Typography } from "@mui/material";

/**
 * Zero rows from a request that **succeeded**.
 *
 * The third of the three sentences, and the one most likely to be written as
 * "No results." That string is a lie by omission on a governance console:
 * three different people's decisions produce an empty screen, and only one of
 * them is about the person reading it.
 *
 * - The app was never allowed to ask — that is `Refusal`, and it never
 *   reaches here.
 * - The app may ask, this person holds none of the tags — an empty
 *   intersection, which is *correct behaviour* and reads as breakage.
 * - Nothing has been written yet — a fact about the store, not about anyone's
 *   permissions.
 *
 * So the heading is a claim and the body has to say which claim. `tagged`
 * narrows it to the filter that produced it, because "no rows" after
 * selecting a tag is the case people reliably misread as a bug: the kernel
 * returns empty rather than 403 there **on purpose** — saying "you are not
 * permitted `customer`" would tell the caller that customers exist, which is
 * the thing the permission was withholding.
 *
 * `severity` is `info`, never `warning`. An empty result is not a problem
 * with anything, and the amber that says "look at this" is spent on states
 * that need looking at.
 */
export default function EmptyState({
  title = "Nothing here",
  tagged,
  children,
  sx,
}) {
  return (
    <Alert severity="info" variant="outlined" sx={sx}>
      <AlertTitle>{title}</AlertTitle>
      {children || (
        <Typography variant="body2">
          {tagged ? (
            <>
              This app declares <code>{tagged}</code>, so this is an empty
              intersection rather than a refusal. Filtering to a tag you
              cannot read returns nothing — not an error, and not everything.
            </>
          ) : (
            <>
              The request succeeded and matched no records. That is a fact
              about what has been written, not about what you are allowed to
              see — a refusal would have said so, and named the declaration
              that produced it.
            </>
          )}
        </Typography>
      )}
    </Alert>
  );
}
